import {
  EditableTable,
  RegAuthHeaderCell,
} from "../generic/editableTable/editableTable.component";
import { Table } from "rsuite";
import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { SvgEdit, SvgBin, SvgSendEmail, SvgView } from "../../assets/icons";
import { CellProps, Badge } from "rsuite";
import { CustomModal } from "../modal/customModal.component";
import { Link } from "react-router-dom";
import { AppRoutesUI } from "../../config/appRoutes";
import { ApiSchemaUser } from "../../models/api/apiSchemaUser";
import { showSuccessToast, showErrorToast } from "../generic/toast.component";
import { ApiUser } from "../../core/apis/user";
import { useNavigate } from "react-router-dom";
import { DateCell } from "../generic/editableTable/dateCell.component";
import { BadgeCell } from "../generic/editableTable/badgeCell.component";
import { useAppDispatch } from "../../core/redux/reduxStore";
import { ActionUI } from "../../core/redux/slices/sliceUI";
import { useHeightUptoBottom } from "../hooks/useHeightToBottomById";

const { Column, Cell } = Table;
type TableData = ApiSchemaUser & { serial: number };

export type UserFilterType = "all" | "paid" | "regbrief" | "trial";

const ActionCell = ({
  rowData,
  onChange,
  onDeleteChange,
  ...props
}: CellProps & {
  onChange: (id: string, isPaid: boolean) => void;
  onDeleteChange: (id: string) => void;
}) => {
  const navigate = useNavigate();
  const [statusLoading, setStatusLoading] = useState(false);
  const handleSubscription = async (id: string) => {
    setStatusLoading(true);
    try {
      await ApiUser.subscribeUser(id, true);
      onChange(id, true);
      navigate(AppRoutesUI.Users.UsersList());
      showSuccessToast("User Successfully Subscribed");
    } catch (error: any) {
      showErrorToast(error.response?.data?.Message || error.message);
    }
    setStatusLoading(false);
  };

  const handleRenew = async (id: string) => {
    setStatusLoading(true);
    try {
      await ApiUser.renewUser(id);
      navigate(AppRoutesUI.Users.UsersList());
      showSuccessToast("Successfully Renewed");
    } catch (error: any) {
      showErrorToast(error.response?.data?.Message || error.message);
    }
    setStatusLoading(false);
  };
  const handleUnableRenew = () => {
    return;
  };

  const handleUserActivation = async (id: string) => {
    setStatusLoading(true);
    try {
      await ApiUser.activateUser(id);
      navigate(AppRoutesUI.Users.UsersList());
      showSuccessToast("Successfully Email sent to the user");
    } catch (error: any) {
      showErrorToast(error.response?.data?.Message || error.message);
    }
    setStatusLoading(false);
  };
  const dispatch = useAppDispatch();
  return (
    <Cell {...props}>
      <div className="d-flex align-items-center ms-5">
        <Link
          to={AppRoutesUI.Users.UsersEditById(rowData.Id)}
          state={rowData.Id}
          className="me-2"
        >
          <SvgEdit />
        </Link>
        <CustomModal
          className="me-2"
          component={<SvgBin />}
          title="Delete User"
          okButton="Delete"
          onOk={() => onDeleteChange(rowData.Id)}
        >
          <div>
            <p>Are you sure you want to delete this User?</p>
          </div>
        </CustomModal>
        {rowData.IsPaid === true ? (
          <CustomModal
            className="me-2"
            component={
              <Badge
                className={
                  !rowData.CanRenew
                    ? "renew-badge-disable px-2"
                    : " renew-badge-able px-2"
                }
                content={
                  <div className="d-flex flex-row align-items-center justify-content-center">
                    <p>Renew</p>
                  </div>
                }
              />
            }
            title="Renew User"
            okButton={rowData.CanRenew ? "Renew" : "Ok"}
            onOk={
              rowData.CanRenew
                ? () => handleRenew(rowData.Id)
                : () => handleUnableRenew()
            }
          >
            {rowData.CanRenew ? (
              <div>
                <p>Are you sure you want to Renew this User?</p>
              </div>
            ) : (
              <div>
                <p>You can't renew this user right now</p>
              </div>
            )}
          </CustomModal>
        ) : (
          <CustomModal
            component={
              <Badge
                className="subscribe-badge me-2"
                content={
                  <div className="d-flex flex-row align-items-center justify-content-center">
                    <p>Subscribe</p>
                  </div>
                }
              />
            }
            title="Add subscription"
            okButton="Subscribe"
            onOk={() => handleSubscription(rowData.Id)}
          >
            <div>
              <p>Are you sure you want to subscribe this User?</p>
            </div>
          </CustomModal>
        )}

        {rowData.IsActive === false ? (
          <div
            onClick={() =>
              dispatch(
                ActionUI.setAddUserEmailModalView({
                  link: rowData.ActivationUrl,
                  show: true,
                })
              )
            }
          >
            <SvgView />
          </div>
        ) : (
          <></>
        )}

        {/* {rowData.IsActive === false ? (
          <CustomModal
            component={<SvgSendEmail />}
            title="Send Email To User"
            okButton="Send"
            onOk={() => handleUserActivation(rowData.Id)}
          >
            <div>
              <p>Thanks for registering with us.Please click on Send button for sending email to user for activation.</p>
            </div>
          </CustomModal>
        ) : (
          <></>
        )} */}
      </div>
    </Cell>
  );
};

export const UsersTable = ({ searchKey, filterType }: { searchKey: string; filterType: UserFilterType }) => {
  const [data, setData] = useState<TableData[]>();
  const [loading, setIsLoading] = useState(true);
  const handleDelete = async (id: string) => {
    try {
      await ApiUser.deleteUser(id);
      getUserList();
      showSuccessToast("User deleted Successfully");
    } catch (error: any) {
      showErrorToast(error.response?.data?.Message || error.message);
    }
  };

  const applyFilter = useCallback((users: TableData[]): TableData[] => {
    switch (filterType) {
      case "paid":
        return users.filter((user) => user.IsPaid === true);
      case "regbrief":
        return users.filter(
          (user) =>
            user.IsPaid === false &&
            user.WTAUserType === "None" &&
            user.TPAUserType === "None"
        );
      case "trial":
        return users.filter((user) => user.IsPaid === false);
      default:
        return users;
    }
  }, [filterType]);

  const fillData = useMemo(
    () => {
      let filtered = data?.filter((item) =>
        item.FullName?.toLowerCase().includes(searchKey.toLowerCase())
      ) || [];
      return applyFilter(filtered);
    },
    [data, searchKey, applyFilter]
  );

  useEffect(() => {
    setIsLoading(true);
    getUserList();
  }, []);

  const getUserList = () => {
    ApiUser.getUserList().then((res) => {
      setData(res.Data?.map((item, index) => ({ ...item, serial: index + 1 })));
      setIsLoading(false);
    });
  };

  const userTableId = "user-table-id";
  const tableHeight = useHeightUptoBottom(userTableId).height;
  return (
    <div id={userTableId}>
      <EditableTable
        className="flex-grow-1 align-items-center justify-content-center"
        height={parseFloat(tableHeight)}
        data={fillData}
        loading={loading}
      >
        <Column>
          <RegAuthHeaderCell>SL No</RegAuthHeaderCell>
          <Cell dataKey="serial" />
        </Column>
        <Column flexGrow={1}>
          <RegAuthHeaderCell>Name</RegAuthHeaderCell>
          <Cell dataKey="FullName" className="table-name-data" />
        </Column>
        <Column flexGrow={1}>
          <RegAuthHeaderCell>Email</RegAuthHeaderCell>
          <Cell dataKey="Email" />
        </Column>
        <Column flexGrow={1}>
          <RegAuthHeaderCell>Registration Date</RegAuthHeaderCell>
          <DateCell dataKey="CreateDate" />
        </Column>
        <Column flexGrow={1} align="center">
          <RegAuthHeaderCell>Status</RegAuthHeaderCell>
          <BadgeCell dataKey="IsPaid" />
        </Column>
        <Column flexGrow={1} align="center" fixed="right">
          <RegAuthHeaderCell>Action</RegAuthHeaderCell>
          <ActionCell
            onDeleteChange={handleDelete}
            onChange={(id: string, isPaid: boolean) => {
              setData((prev) =>
                prev?.map((item) =>
                  item.Id === id ? { ...item, IsPaid: isPaid } : item
                )
              );
            }}
          />
        </Column>
      </EditableTable>
    </div>
  );
};
